import { TextField, CircularProgress, InputAdornment, MenuItem } from '@mui/material';
import React from 'react';
import ShippingDetails from '../../../../sharedComponents/ShippingDetails';
import { TrashIcon, CheckCircleIcon, ExclamationCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const statusConfig = {
  success:     { icon: CheckCircleIcon,      color: 'text-green-500',  title: 'ดึงข้อมูลราคาสำเร็จ' },
  unsupported: { icon: ExclamationCircleIcon, color: 'text-amber-400',  title: 'ไม่รองรับ URL นี้ — กรุณาใส่ราคาเอง' },
  error:       { icon: XCircleIcon,          color: 'text-red-500',    title: 'ดึงข้อมูลไม่สำเร็จ' },
};

const AmountCalItem = ({ data, handleInputChange, handleShippingChange, handleRemoveInput, index, canRemove, isLoading, fetchStatus, botBuyers = [] }) => {
  const isDeliveryFeeDisabled = data.shipDetail && ['free', 'unknown', 'cod'].includes(data.shipDetail);
  const statusInfo = !isLoading && fetchStatus ? statusConfig[fetchStatus] : null;
  // Mercari item: the bot queues the card after payment; the admin may pin it to one buyer here
  // ("ส่งการ์ดให้คนนี้เท่านั้น", owner 2026-11-02). Saved on the link item, pinned when the order is born.
  const isMercari = /mercari/i.test(data.url || '');

  return (
    <div className={`flex flex-col gap-3 mt-3 p-4 border rounded-lg transition-colors ${isLoading ? 'border-purple-300 bg-purple-50' : fetchStatus === 'unsupported' ? 'border-amber-200' : fetchStatus === 'error' ? 'border-red-200' : fetchStatus === 'success' ? 'border-green-200' : 'border-gray-200'}`}>
      <div className='flex gap-x-3 items-center'>
        <span className='bg-red-300 w-8 h-8 rounded-full flex items-center justify-center my-auto'>{index + 1}</span>
        <div className="relative">
          <TextField 
            label="Link" 
            onChange={(e) => handleInputChange(e, 'url', index)}
            value={data.url || ''}
            sx={{ width: 250 }}
            InputProps={isLoading ? {
              endAdornment: (
                <InputAdornment position="end">
                  <CircularProgress size={16} sx={{ color: '#7c3aed' }} />
                </InputAdornment>
              )
            } : undefined}
          />
          {statusInfo && (
            <statusInfo.icon
              className={`absolute -top-2 -right-2 h-5 w-5 ${statusInfo.color} bg-white rounded-full`}
              title={statusInfo.title}
            />
          )}
          {data.appraisalFee != null && (
            <label
              className={`absolute -bottom-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border cursor-pointer ${
                Number(data.appraisalFee) > 0
                  ? 'bg-amber-100 text-amber-800 border-amber-300'
                  : 'bg-green-100 text-green-800 border-green-300'
              }`}
              title='สินค้านี้ส่งตรวจได้ (ตรวจสอบของแท้) — ติ๊กเพื่อรวมค่าส่งตรวจในสรุปยอด'
            >
              <input
                type='checkbox'
                checked={!!data.includeInspection}
                onChange={(e) => handleInputChange({ target: { value: e.target.checked } }, 'includeInspection', index)}
                className='w-3 h-3'
              />
              ส่งตรวจ {Number(data.appraisalFee) > 0 ? `¥${data.appraisalFee}` : 'ฟรี'}
            </label>
          )}
        </div>
        <TextField 
          label="ค่าสินค้า" 
          onChange={(e) => handleInputChange(e, 'amount', index)}
          value={data.amount || ''}
          sx={{ width: 150 }}
        />
        <TextField 
          label="ค่าส่งในญี่ปุ่น"
          onChange={(e) => handleInputChange(e, 'deliveryFeeJp', index)}
          value={data.deliveryFeeJp || ''}
          sx={{ width: 150 }}
          disabled={isDeliveryFeeDisabled}
          placeholder="Delivery Fee"
        />
        <TextField 
          label="จำนวน" 
          onChange={(e) => handleInputChange(e, 'qty', index)}
          value={data.qty || ''}
          sx={{ width: 100 }}
        />
        {canRemove && (
          <button
            onClick={() => handleRemoveInput(index)}
            className="ml-auto p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
            title="Remove row"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        )}
      </div>
      
      {/* Mercari: the bot queues the card after payment; pin it to one buyer here. Own line, so the
          price row keeps its width. */}
      {isMercari && botBuyers.length > 0 && (
        <div className='ml-11 flex items-center gap-2'>
          <span className='text-sm text-gray-600'>ส่งการ์ดให้</span>
          <TextField
            select
            size="small"
            value={data.dispatchPinnedBuyerId || ''}
            onChange={(e) => handleInputChange({ target: { value: e.target.value || null } }, 'dispatchPinnedBuyerId', index)}
            SelectProps={{ displayEmpty: true }}
            sx={{ width: 220 }}
            title="ตามคิวบอท = บอทเลือกคนกดตามคิว · เลือกคน = ส่งการ์ดให้คนนี้เท่านั้น"
          >
            <MenuItem value="">ตามคิวบอท</MenuItem>
            {botBuyers.map((b) => <MenuItem key={b.id} value={b.id}>{b.label} เท่านั้น</MenuItem>)}
          </TextField>
        </div>
      )}

      {/* Add ShippingDetails component */}
      <div className='ml-11'>
        <ShippingDetails 
          order={data} 
          onButtonClick={(shipDetail) => handleShippingChange(shipDetail, index)} 
        />
      </div>
    </div>
  );
};

export default AmountCalItem;
